import React from "react";
import { getSupabase } from "../lib/supabaseClient";
import { toClient } from "../lib/records";

const AuthContext = React.createContext(null);

async function loadProfile() {
  const supabase = getSupabase();
  const { data: sessionData } = await supabase.auth.getSession();
  const userId = sessionData.session?.user?.id;
  if (!userId) return null;
  const { data, error } = await supabase.from("profiles").select("*").eq("id", userId).maybeSingle();
  if (error || !data || data.is_active === false) return null;
  return toClient(data);
}

export function AuthProvider({ children }) {
  const [session, setSessionState] = React.useState(null);
  const [user, setUser] = React.useState(null);
  const [authReady, setAuthReady] = React.useState(false);

  React.useEffect(() => {
    let active = true;
    const supabase = getSupabase();

    supabase.auth.getSession().then(async ({ data }) => {
      if (!active) return;
      setSessionState(data.session);
      if (data.session) {
        const profile = await loadProfile();
        if (active) setUser(profile);
      }
      if (active) setAuthReady(true);
    });

    const { data: subscription } = supabase.auth.onAuthStateChange(async (_event, nextSession) => {
      setSessionState(nextSession);
      if (!nextSession) {
        setUser(null);
        return;
      }
      const profile = await loadProfile();
      setUser(profile);
    });

    return () => {
      active = false;
      subscription.subscription.unsubscribe();
    };
  }, []);

  const setSession = React.useCallback((nextToken, nextUser) => {
    if (!nextToken) {
      getSupabase().auth.signOut();
      setSessionState(null);
      setUser(null);
      return;
    }
    if (nextUser) setUser(nextUser);
  }, []);

  const logout = React.useCallback(async () => {
    await getSupabase().auth.signOut();
    setSessionState(null);
    setUser(null);
  }, []);

  const value = React.useMemo(
    () => ({
      token: session?.access_token || null,
      user,
      setUser,
      authReady,
      isAuthenticated: Boolean(session),
      setSession,
      logout,
    }),
    [session, user, authReady, setSession, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = React.useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
